import {useCallback, useEffect, useState, type Dispatch, type SetStateAction} from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductByFranchiseStore } from '../../../store/useProduct';
import { useFranchiseStore } from '../../../store/useFranchise'
import { useLoginStore } from '../../../store/useLogin';
import LazyImage from '../../form/LazyImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInfoCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import FileUpload from '../../form/FileUpload';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';

// 상품 기본정보
const InfoPanel = ({
    productDetail,
    franchiseDetail,
}: {
    productDetail: any;
    franchiseDetail: any;
}) => {
    return (
        <div className="bg-white rounded-lg p-5">
            <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                    <h1 className="text-xl font-bold text-gray-900">
                        {productDetail?.productName || <div className="h-7 w-3/4 animate-pulse bg-gray-200 rounded"></div>}
                    </h1>
                    {productDetail?.confirmYn === 'Y' && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">활성화</span>
                    )}
                </div>
                <p className="text-sm text-gray-600">코드: {productDetail?.productCode || '-'}</p>
            </div>

            <div className="space-y-4">
                <div>
                    <h3 className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-gray-400" />
                        가맹점 정보
                    </h3>
                    <div className="pl-6 space-y-2.5">
                        <div className="flex items-start text-sm min-h-[1.5rem]">
                            <span className="text-gray-500 w-24 flex-shrink-0">가맹점 이름</span>
                            <span className="text-gray-700 flex-1">{franchiseDetail?.companyName || '-'}</span>
                        </div>
                        <div className="flex items-center text-sm min-h-[1.5rem]">
                            <span className="text-gray-500 w-24 flex-shrink-0">사업자번호</span>
                            <span className="text-gray-700 flex-1">{franchiseDetail?.companyNumber || '-'}</span>
                        </div>
                        <div className="flex items-center text-sm min-h-[1.5rem]">
                            <span className="text-gray-500 w-24 flex-shrink-0">대표 전화</span>
                            <span className="text-gray-700 flex-1">{franchiseDetail?.telNo || '-'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 공통 기존 이미지 섹션
const ExistingImageSection = ({
    title,
    url,
    isSelected,
    onClick,
    selectedText,
    buttonText,
}: {
    title: string;
    url?: string;
    isSelected: boolean;
    onClick: () => void;
    selectedText: string;
    buttonText: string;
}) => {
    if (!url) return null;
    return (
        <div className="bg-white rounded-lg p-3">
            <div className="p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
                <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-white border border-gray-200">
                        <LazyImage src={url} alt={title} className="w-full h-full object-cover" />
                    </div>
                    <button
                        onClick={onClick}
                        className={`px-3 py-1.5 text-sm rounded transition-colors ${
                            isSelected ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        disabled={isSelected}
                    >
                        {isSelected ? selectedText : buttonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

// 공통 업로드 섹션
const UploadSection = ({
    title,
    setFiles,
    showCancel,
    onCancel,
}: {
    title: string;
    setFiles: Dispatch<SetStateAction<File[]>>;
    showCancel: boolean;
    onCancel: () => void;
}) => {
    return (
        <div className="mb-4 mt-6">
            <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
                <div className="!aspect-square !border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors">
                    <FileUpload setFiles={setFiles} accept="image/*" multiple={false} />
                </div>
                {showCancel && (
                    <button onClick={onCancel} className="mt-2 text-sm text-red-600 hover:text-red-700">취소</button>
                )}
            </div>
        </div>
    );
};

// 공통 메인 이미지 프리뷰
const ImagePreview = ({ src, alt }: { src?: string | null; alt?: string }) => {
    const resolved = src ?? undefined;
    return (
        <div className="bg-white rounded-lg p-3">
            <div className="relative w-full max-w-[400px] mx-auto">
                <div className="rounded-lg overflow-hidden bg-gray-50" style={{ aspectRatio: '210/297' }}>
                    {resolved ? (
                        <LazyImage src={resolved} alt={alt ?? ''} className="w-full h-full object-contain" />
                    ) : (
                        <div className="w-full h-full animate-pulse bg-gray-200"></div>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * @author taulmax
 * @createDate 2024.10.08
 * @updateDate 2024.10.22
 * @description 상품 이미지 상세 페이지
 */
const ProductImage = () => {
    const navigate = useNavigate();
    // URL에서 직접 파라미터 추출
    const pathname = window.location.pathname;
    const pathParts = pathname.split('/');
    const productId = pathParts[2] || '';
    const franchiseId = pathParts[3] || '';

    const { productDetail, getProductDetail } = useProductByFranchiseStore();
    const { franchiseDetail, getFranchiseDetail } = useFranchiseStore();

    const [thumbnailFiles, setThumbnailFiles] = useState<File[]>([]);
    const [endFiles, setEndFiles] = useState<File[]>([]);
    const [newThumbnail, setNewThumbnail] = useState<{file: File; id: string} | null>(null);
    const [inlineFiles, setInlineFiles] = useState<File[]>([]);
    const [newInline, setNewInline] = useState<{file: File; id: string} | null>(null);
    const [newEnd, setNewEnd] = useState<{file: File; id: string} | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewInlineUrl, setPreviewInlineUrl] = useState<string | null>(null);
    const [previewEndUrl, setPreviewEndUrl] = useState<string | null>(null);
    const [deleteThumbnailId, setDeleteThumbnailId] = useState<string>("");
    const [deleteInlineId, setDeleteInlineId] = useState<string>("");
    const [deleteEndId, setDeleteEndId] = useState<string>("");
    const [isBasicImageSelected, setIsBasicImageSelected] = useState<boolean>(false);
    const [isBasicInlineSelected, setIsBasicInlineSelected] = useState<boolean>(false);
    const [isBasicEndSelected, setIsBasicEndSelected] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'thumbnail' | 'inline' | 'end'>('thumbnail');

    const { role } = useLoginStore();

    useEffect(() => {
        if (productId) {
            getProductDetail(productId, franchiseId);
        }
    }, [getProductDetail, productId]);

    useEffect(() => {
        if (franchiseId) {
            getFranchiseDetail(franchiseId);
        }
    }, [getFranchiseDetail,franchiseId]);

    // A4 비율 검증 함수
    const validateA4Ratio = (width: number, height: number): boolean => {
        const ratio = height / width;
        const a4Ratio = 1.414; // A4 비율 (297/210)
        const tolerance = 0.05; // 5% 오차 허용
        return Math.abs(ratio - a4Ratio) <= tolerance;
    };

    // 썸네일 업로드 처리
    useEffect(() => {
        const uploadThumbnail = async () => {
            if (thumbnailFiles.length > 0) {
                const file = thumbnailFiles[0];

                // 이미지 치수 검증
                const img = new Image();
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    img.onload = async () => {
                        // A4 비율 검증
                        if (!validateA4Ratio(img.width, img.height)) {
                            toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
                            setThumbnailFiles([]);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('file', file);

                        try {
                            const response = await apiClient.post('/upload-file/upload', formData);
                            setNewThumbnail({ file, id: response.data.id });
                            setPreviewUrl(response.data.fileFullUrl);

                            setThumbnailFiles([]); // 업로드 후 초기화
                            setIsBasicImageSelected(false); // 새 이미지 업로드 시 기존 이미지 선택 해제
                            toast.success('이미지가 업로드되었습니다.');
                        } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            toast.error('이미지 업로드를 실패했습니다.');
                        }
                    };

                    img.src = e.target?.result as string;
                };

                reader.readAsDataURL(file);
            }
        };

        uploadThumbnail();
    }, [thumbnailFiles, productDetail, deleteThumbnailId]);

    useEffect(() => {
        const uploadInline = async () => {
            if (inlineFiles.length > 0) {
                const file = inlineFiles[0];

                // 이미지 치수 검증
                const img = new Image();
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    img.onload = async () => {
                        // A4 비율 검증
                        if (!validateA4Ratio(img.width, img.height)) {
                            toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
                            setInlineFiles([]);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('file', file);

                        try {
                            const response = await apiClient.post('/upload-file/upload', formData);
                            setNewInline({ file, id: response.data.id });
                            setPreviewInlineUrl(response.data.fileFullUrl);


                            setInlineFiles([]); // 업로드 후 초기화
                            setIsBasicInlineSelected(false); // 새 이미지 업로드 시 기존 이미지 선택 해제
                            toast.success('이미지가 업로드되었습니다.');
                        } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            toast.error('이미지 업로드를 실패했습니다.');
                        }
                    };

                    img.src = e.target?.result as string;
                };

                reader.readAsDataURL(file);
            }
        };

        uploadInline();
    }, [inlineFiles, productDetail, deleteInlineId]);

    // 맺음말 업로드 처리
    useEffect(() => {
        const uploadEnd = async () => {
            if (endFiles.length > 0) {
                const file = endFiles[0];

                // 이미지 치수 검증
                const img = new Image();
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    img.onload = async () => {
                        // A4 비율 검증
                        if (!validateA4Ratio(img.width, img.height)) {
                            toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
                            setEndFiles([]);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('file', file);

                        try {
                            const response = await apiClient.post('/upload-file/upload', formData);
                            setNewEnd({ file, id: response.data.id });
                            setPreviewEndUrl(response.data.fileFullUrl);

                            setEndFiles([]); // 업로드 후 초기화
                            setIsBasicEndSelected(false); // 새 이미지 업로드 시 기존 이미지 선택 해제
                            toast.success('이미지가 업로드되었습니다.');
                        } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            toast.error('이미지 업로드를 실패했습니다.');
                        }
                    };

                    img.src = e.target?.result as string;
                };

                reader.readAsDataURL(file);
            }
        };

        uploadEnd();
    }, [endFiles, productDetail, deleteEndId]);

    // 기존 이미지로 돌아가기 핸들러
    const handleRevertToBasicImage = useCallback(() => {
        if (productDetail?.basicThumbnailFile && productDetail?.thumbnailFile) {
            // 현재 썸네일 ID를 삭제할 ID로 설정
            setDeleteThumbnailId(productDetail.thumbnailFile.id);

            // 미리보기를 기존 이미지로 설정
            setPreviewUrl(productDetail.basicThumbnailFile.fileFullUrl);
            setIsBasicImageSelected(true);
            setNewThumbnail(null)

            toast.success('기존 이미지로 변경되었습니다. 저장 버튼을 눌러 확정하세요.');
        }
    }, [productDetail]);

    const handleRevertToBasicInline = useCallback(() => {
        if (productDetail?.basicInlineFile && productDetail?.inlineFile) {
            // 현재 썸네일 ID를 삭제할 ID로 설정
            setDeleteInlineId(productDetail.inlineFile.id);

            // 미리보기를 기존 이미지로 설정
            setPreviewInlineUrl(productDetail.basicInlineFile.fileFullUrl);
            setIsBasicInlineSelected(true);
            setNewInline(null)

            toast.success('기존 이미지로 변경되었습니다. 저장 버튼을 눌러 확정하세요.');
        }
    }, [productDetail]);

    const handleRevertToBasicEnd = useCallback(() => {
        if (productDetail?.basicEndFile && productDetail?.basicEndFile && productDetail?.endFile) {
            // 현재 썸네일 ID를 삭제할 ID로 설정
            setDeleteEndId(productDetail?.endFile.id);

            // 미리보기를 기존 이미지로 설정
            setPreviewEndUrl(productDetail.basicEndFile.fileFullUrl);
            setIsBasicEndSelected(true);
            setNewEnd(null)

            toast.success('기존 이미지로 변경되었습니다. 저장 버튼을 눌러 확정하세요.');
        }
    }, [productDetail]);


    // 저장 버튼 클릭 핸들러
    const handleSaveImages = async () => {
        if (!productId) {
            toast.error('상품 ID가 없습니다.');
            return;
        }

        let thumbnailFileId: string | null = null;
        let inlineFileId: string | null = null;
        let endId: string | null = null;

        // 새 이미지가 업로드되었을 때
        if (newThumbnail) {
            thumbnailFileId = newThumbnail.id;
        }
        if (newInline) {
            inlineFileId = newInline.id;
        }
        if (newEnd) {
            endId = newEnd.id;
        }

        // 기존 이미지로 돌아가기를 선택했을 때
        if (isBasicImageSelected && productDetail?.basicThumbnailFile) {
            thumbnailFileId = productDetail.basicThumbnailFile.id;
        }
        if (isBasicInlineSelected && productDetail?.basicInlineFile) {
            inlineFileId = productDetail.basicInlineFile.id;
        }
        if (isBasicEndSelected && productDetail?.basicEndFile) {
            endId = productDetail.basicEndFile.id;
        }

        try {
            const response = await apiClient.post(
                `/product/update-image/${productId}`,
                {
                    thumbnailFileId: thumbnailFileId,
                    deleteThumbnailId: deleteThumbnailId || undefined,
                    inlineFileId: inlineFileId,
                    deleteInlineId: deleteInlineId || undefined,
                    endFileId: endId,
                    deleteEndId: deleteEndId || undefined,
                    franchiseId: franchiseId
                }
            );
            if (response.status === 200) {
                toast.success('이미지가 성공적으로 저장되었습니다!');
                setNewThumbnail(null);
                setPreviewUrl(null);
                setDeleteThumbnailId("");
                setNewInline(null);
                setPreviewInlineUrl(null);
                setDeleteInlineId("");
                setIsBasicImageSelected(false);
                setIsBasicInlineSelected(false);
                setNewEnd(null);
                setPreviewEndUrl(null);
                setDeleteEndId("");
                setIsBasicEndSelected(false);
                getProductDetail(productId, franchiseId);
            }
        } catch (error) {
            console.error('이미지 저장 실패:', error);
            toast.error('이미지 저장에 실패했습니다.');
        }
    };

    const hasChanges = newThumbnail || deleteThumbnailId || newInline || deleteInlineId || newEnd || deleteEndId;

    return (
        <div>
            {/* 상단 네비게이션 및 저장 버튼 */}
            <div className="border-b sticky top-0">
                <div className="max-w-5xl mx-auto px-4 py-1">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate('/product/thumbnail')}
                            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="w-3 h-3" />
                            <span>상품 확인으로 돌아가기</span>
                        </button>

                        {role === 'ROLE_ADMIN' && (
                            <button
                                onClick={handleSaveImages}
                                disabled={!hasChanges}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                                    hasChanges
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                <FontAwesomeIcon icon={faSave} />
                                <span>이미지 저장</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* 메인 콘텐츠 */}
            <div className="max-w-5xl mx-auto px-4 py-1">
                {/* 탭 메뉴 */}
                <div className="flex border-b mb-6">
                    <button
                        onClick={() => setActiveTab('thumbnail')}
                        className={`px-6 py-3 font-medium transition-colors relative ${
                            activeTab === 'thumbnail'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        표지 이미지
                        {(newThumbnail || deleteThumbnailId) && (
                            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('inline')}
                        className={`px-6 py-3 font-medium transition-colors relative ${
                            activeTab === 'inline'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        속지 이미지
                        {(newInline || deleteInlineId) && (
                            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                        )}
                    </button>
                    {productDetail?.endFile ? (
                        <button
                            onClick={() => setActiveTab('end')}
                            className={`px-6 py-3 font-medium transition-colors relative ${
                                activeTab === 'end'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            맺음말 이미지
                            {(newEnd || deleteEndId) && (
                                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></span>
                            )}
                        </button>
                    ) : null}
                </div>

                {/* 탭 콘텐츠 */}
                {activeTab === 'thumbnail' ? (
                    <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                        {/* 이미지 영역 */}
                        <div className="space-y-2">
                            {/* 메인 이미지 */}
                            <ImagePreview
                                src={previewUrl != null ? previewUrl : productDetail?.thumbnailFile?.fileFullUrl}
                                alt={productDetail?.productName}
                            />

                            {/* 기존 이미지 섹션 */}
                            {productDetail?.basicThumbnailFile?.fileFullUrl && (
                                <ExistingImageSection
                                    title="기존 표지 이미지"
                                    url={productDetail?.basicThumbnailFile?.fileFullUrl}
                                    isSelected={isBasicImageSelected}
                                    onClick={handleRevertToBasicImage}
                                    selectedText="기존 표지 이미지 선택됨"
                                    buttonText="기존 표지 이미지로 돌아가기"
                                />
                            )}
                        </div>

                        {/* 정보 영역 + 업로드 영역 */}
                        <div className="space-y-4">
                            <InfoPanel productDetail={productDetail} franchiseDetail={franchiseDetail} />
                            <UploadSection
                                title="표지 이미지 업로드"
                                setFiles={setThumbnailFiles}
                                showCancel={Boolean(previewUrl || newThumbnail)}
                                onCancel={() => { setPreviewUrl(null); setNewThumbnail(null); setIsBasicImageSelected(false); }}
                            />
                        </div>
                        </div>
                    </div>
                ) : ( activeTab === 'inline' ? (
                    <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                        {/* 이미지 영역 */}
                        <div className="space-y-2">
                            {/* 메인 이미지 */}
                            <ImagePreview
                                src={previewInlineUrl != null ? previewInlineUrl : productDetail?.inlineFile?.fileFullUrl}
                                alt={productDetail?.productName}
                            />

                            {/* 기존 이미지 섹션 */}
                            {productDetail?.basicInlineFile?.fileFullUrl && (
                                <ExistingImageSection
                                    title="기존 속지 이미지"
                                    url={productDetail?.basicInlineFile?.fileFullUrl}
                                    isSelected={isBasicInlineSelected}
                                    onClick={handleRevertToBasicInline}
                                    selectedText="기존 속지 이미지 선택됨"
                                    buttonText="기존 속지 이미지로 돌아가기"
                                />
                            )}
                        </div>

                        <div className="space-y-4">
                            <InfoPanel productDetail={productDetail} franchiseDetail={franchiseDetail} />
                            <UploadSection
                                title="속지 이미지 업로드"
                                setFiles={setInlineFiles}
                                showCancel={Boolean(previewInlineUrl || newInline)}
                                onCancel={() => { setPreviewInlineUrl(null); setNewInline(null); setIsBasicInlineSelected(false); }}
                            />
                        </div>
                    </div>
                    </div>)
                    : (activeTab === 'end' && productDetail?.endFile ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                            {/* 이미지 영역 */}
                            <div className="space-y-2">
                                {/* 메인 이미지 */}
                                <ImagePreview
                                    src={previewEndUrl != null ? previewEndUrl : productDetail?.endFile?.fileFullUrl}
                                    alt={productDetail?.productName}
                                />

                                {/* 기존 이미지 섹션 */}
                                {productDetail?.basicEndFile?.fileFullUrl && (
                                    <ExistingImageSection
                                        title="기존 맺음말 이미지"
                                        url={productDetail?.basicEndFile?.fileFullUrl}
                                        isSelected={isBasicEndSelected}
                                        onClick={handleRevertToBasicEnd}
                                        selectedText="기존 맺음말 이미지 선택됨"
                                        buttonText="기존 맺음말 이미지로 돌아가기"
                                    />
                                )}
                            </div>

                            {/* 정보 영역 + 업로드 영역 */}
                            <div className="space-y-4">
                                <InfoPanel productDetail={productDetail} franchiseDetail={franchiseDetail} />
                                <UploadSection
                                    title="맺음말 이미지 업로드"
                                    setFiles={setEndFiles}
                                    showCancel={Boolean(previewEndUrl || newEnd)}
                                    onCancel={() => { setPreviewEndUrl(null); setNewEnd(null); setIsBasicEndSelected(false); }}
                                />
                            </div>
                        </div>
                ) : null)
                )}
            </div>
        </div>
    );
};

export default ProductImage;
